import { defineComponent as _defineComponent } from 'vue'
import CustomerType from "@/model/CustomerType";
  import Order from "@/model/Order";
  import CustomerService from "@/service/CustomerService";
  import {computed, onMounted, reactive, ref, watch} from "vue";
  import {VCalendar, VCol} from "vuetify/lib";
  import {t} from "@/mixin/mixins";
  import InvCreateDialog from "@/views/Orders/Calendar/InvCreateDialog.vue";
  import EditMaxPosDialog from "@/views/Orders/Calendar/EditMaxPosDialog.vue";
  import PageUtils from "@/components/PageUtils";
  import OrderServiceV2 from "@/service/OrderServiceV2";
  import CalendarDataService from "@/service/CalendarDataService";
  import FabButton from "@/components/FabButton.vue";
  import moment from "moment";
  import {CalendarData} from "@/model/CalendarData";
  import SettingService from "@/service/SettingService";
  import {OrderType} from "@/enums";
  import Customer from "@/model/Customer";
  import {CalendarEvent} from "vuetify";
  import {useRouter} from "vue-router/composables";
  import MonthPicker from "@/components/MonthPicker.vue";
  import { yearMonth } from "@/DateUtils";

  interface VCalendarInstance extends InstanceType<typeof VCalendar> {
    checkChange: () => void;
    prev: () => void;
    next: () => void;
    move: (amount: number) => void;
    lastStart: { date: string; };
    lastEnd: { date: string; };
    title: string;
  }

  type CustomerCategoryNames = typeof customerCategories[number]["name"];

  
export default /*#__PURE__*/_defineComponent({
  __name: 'OrderCalendar',
  props: {
    customer: {
      type: Customer,
      default: () => undefined
    },
    positions: {
      type: Number,
      default: () => 0
    }
  },
  setup(__props) {

const props = __props

  

  const initialState = {
    yearMonth: moment().format("YYYY-MM"),
    sumPositionsCurrentYearMonth: 0,
    sumPositionsPreviousYearMonth: 0,
  };

  const state = reactive({...initialState});

  watch(() => state.yearMonth, (newValue, oldValue) => {
    let newYearMonth = moment(newValue);
    let oldYearMonth = moment(oldValue);
    let diff = newYearMonth.diff(oldYearMonth, "months");
    calendar.value?.move(diff);
  });

  const clickedOrder = ref<Order | undefined>(undefined);
  const clickedCalendarData = ref<CalendarData | undefined>(undefined);
  const calendarKey = ref<number>(0);
  const calendar = ref<VCalendarInstance | null>(null);
  const focus = ref<string | null>(null);
  const type = ref<"month" | "week" | "day" | "4day">("month");
  const regionFilter = ref("");
  const vbFilter = ref("");
  const kstFilter = ref("");
  const combinedStrings = ref("");
  const customerType = ref<CustomerType | null>(null);
  const customerTypes = ref<CustomerType[]>([]);
  const typeToLabel = {
    month: t("order.calendar.type.month"),
    week: t("order.calendar.type.week"),
    day: t("order.calendar.type.day"),
    "4day": t("order.calendar.type.fourDays")
  };
  const orders = ref<Array<Order>>([]);
  const calendarData = ref<Array<CalendarData>>([]);
  let defaultMaxPos = ref<number>(90000);
  const dataCache: any = [];

  const customerCategories = [
    {
      name: "drinks",
      icon: "mdi-bottle-wine",
    },
    {
      name: "storage",
      icon: "mdi-package-variant-closed",
    },
    {
      name: "targetActualComparison",
      icon: "mdi-bookmark-check-outline",
    },
    {
      name: "labels",
      icon: "mdi-label-multiple-outline",
    }
  ] as const;

  const isCustomerCategoryDefined = (event: Order, categoryName: CustomerCategoryNames ):boolean => {
    if (event.customerCategory && event.customerCategory.hasOwnProperty(categoryName)) {
      return event.customerCategory[categoryName];
    } else {
      return false;
    }
  };

  const title = computed(() => {
    const cal = calendar.value as VCalendarInstance;
    let title = cal.title ? cal.title : "";
    if (type.value != "month") {
      if (cal?.$data) {
        const start = cal.$data["lastStart"];
        const end = cal.$data["lastEnd"];
        if (type.value == "week") {
          title = `W${moment(start.date).format("WW YYYY")}`;
        } else if (type.value == "day") {
          title = moment(start.date).format("DD.MM.YYYY");
        } else if (type.value == "4day") {
          title = `${moment(start.date).format("DD.MM.YYYY")} – ${moment(end.date).format("DD.MM.YYYY")}`;
        }
      }
    }
    return title;
  });

  const weekDayFormat = (day: any) => {
    if (type.value !== "month") {
      return moment(day.date).format("dd, DD.MM.");
    } else {
      return moment(day.date).format("dd");
    }
  };

  const customNext = () => {
    if( type.value == "month") {
      state.yearMonth = moment(calendar.value?.lastStart.date).add(1, "month").format("YYYY-MM");
    }
    else if (type.value == "week") {
      state.yearMonth = moment(calendar.value?.lastStart.date).add(1, "week").format("YYYY-MM");
    }
    else if (type.value == "day") {
      state.yearMonth = moment(calendar.value?.lastStart.date).add(1, "day").format("YYYY-MM");
    }
    else if (type.value == "4day") {
      state.yearMonth = moment(calendar.value?.lastStart.date).add(4, "day").format("YYYY-MM");
    }
    calendar.value?.next();
  };

  const customPrev = () => {
    if( type.value == "month") {
      state.yearMonth = moment(calendar.value?.lastStart.date).subtract(1, "month").format("YYYY-MM");
    }
    else if (type.value == "week") {
      state.yearMonth = moment(calendar.value?.lastStart.date).subtract(1, "week").format("YYYY-MM");
    }
    else if (type.value == "day") {
      state.yearMonth = moment(calendar.value?.lastStart.date).subtract(1, "day").format("YYYY-MM");
    }
    else if (type.value == "4day") {
      state.yearMonth = moment(calendar.value?.lastStart.date).subtract(4, "day").format("YYYY-MM");
    }
    calendar.value?.prev();
  };

  const events = computed(() => {
    calendarKey.value;
    let filteredArray = orders.value.filter((order) => {
      let regionMatch = !regionFilter.value || (order.customer && order.customer.region && Number(order.customer.region) === Number(regionFilter.value));
      let region2Match = !vbFilter.value || (order.customer && order.customer.sellingArea && Number(order.customer.sellingArea) === Number(vbFilter.value));
      let region3Match = !kstFilter.value || (order.customer && order.customer.costCenter && Number(order.customer?.costCenter) === Number(kstFilter.value));
      let region4Match = !customerType.value || (order.customer && order.customer.customerType && Number(order.customer?.customerType.id) === (Number(customerType.value) || Number(customerType.value.id)));
      return regionMatch && region2Match && region3Match && region4Match;
    }).map((order) => ({
      orderId: order.id,
      name: order.name,
      start: order.validFrom,
      end: order.validTo,
      color: "#939d00",
      notes: order.notes,
      customerCategory: order.customerCategory
    }));
    return filteredArray;
  });

  const loadDefaultMaxPos = () => {
    SettingService.getByName("defaultMaxPos")
      .then(response => {
        if (response.data) {
          const dataStr = response.data.value as string;
          defaultMaxPos.value = parseInt(dataStr);
        }
      });
  };

  const loadOrders = (firstDay: string, lastDay: string) => {
    OrderServiceV2.findOrdersInTimeframe(firstDay, lastDay, "notes", OrderType.INV)
      .then(response => {
        const ordersOfTheMonth = PageUtils.extractCollection<Order>(response.data, "orders");

        ordersOfTheMonth.forEach((item) => {
          let index = orders.value.findIndex((obj) => obj.id === item.id);
          if (index === -1) {
            orders.value.push(item);
          }
        });
      })
      .then(() => {
        dataCache.push(currentMonthYear.value?.yearMonth);
      });
  };

  const exportOrders = () => {
    if (calendar.value) {
      return OrderServiceV2.exportOrdersInTimeframe(calendar.value?.lastStart.date, calendar.value?.lastEnd.date, OrderType.INV);
    }
  };

  const loadCalendarData = (from: string, to: string) => {
    CalendarDataService.findCalendarData(from, to)
      .then(response => {
        const calendarDataOfTheMonth = PageUtils.extractCollection<CalendarData>(response.data, "calendarData");

        // Get the sum of positions for the current month
        state.sumPositionsCurrentYearMonth = calendarDataOfTheMonth
          .map(entry => entry.positions ?? 0)
          .reduce((prev, cur) => prev + cur, 0);

        calendarDataOfTheMonth.forEach((item) => {
          const index = calendarData.value.findIndex((obj) => obj.date === item.date);
          if (index !== -1) {
            calendarData.value.splice(index, 1);
          }
          calendarData.value.push(item);
        });
      })
      .then(() => {
        dataCache.push(currentMonthYear.value?.yearMonth);
      });

    // Get the sum of positions of the same month of the previous year
    let previousYearFrom = moment(from).subtract(1, "years").format("YYYY-MM-DD");
    let previousYearTo = moment(to).subtract(1, "years").format("YYYY-MM-DD");
    CalendarDataService.findCalendarData(previousYearFrom, previousYearTo)
      .then(response => {
        const calendarDataOfTheMonth = PageUtils.extractCollection<CalendarData>(response.data, "calendarData");

        state.sumPositionsPreviousYearMonth = calendarDataOfTheMonth
          .map(entry => entry.positions ?? 0)
          .reduce((prev, cur) => prev + cur, 0);
      });
  };

  const currentMonthYear = computed(() => {
    if (focus.value) {
      let yearMonth = moment(focus.value).format("YYYY-MM");
      return {yearMonth};
    }
    return null;
  });

  const inventoryCount = computed(() => {
    return orders.value
      .filter(order => order.customer)
      .length;
  });

  watch(() => currentMonthYear.value, (newValue: any, oldValue: any) => {
    let yearMonth = moment(currentMonthYear.value?.yearMonth, "YYYY-MM");

    let formattedFirstDay = yearMonth.startOf("month").format("YYYY-MM-DD");
    const formattedLastDay = yearMonth.endOf("month").format("YYYY-MM-DD");

    if (!dataCache.includes(newValue)) {
      loadOrders(formattedFirstDay, formattedLastDay);
      loadCalendarData(formattedFirstDay, formattedLastDay);
    }
  });

  onMounted(() => {
    calendar.value?.checkChange();
    CustomerService.getCustomerTypes().then(data => {
      customerTypes.value = data;
      setDefaultCustomerType(customerTypes.value);
    });
    focus.value = new Date().toISOString().substr(0, 10);

    loadDefaultMaxPos();
  });

  const setDefaultCustomerType = (types: CustomerType[]) => {
    const type = types.find(t => t.name == "REWE");
    if (type) {
      customerType.value = type;
    }
  };

  const viewDay = ({date}: { date: any }) => {
    focus.value = date;
    type.value = "day";
  };

  const setToday = () => {
    focus.value = "";
  };

  const pattern = /^((\d{2}( \d{2}( \d{4})?)?)?)?$/;

  const maskRule = (v: any) => {
    return pattern.test(v) || "Invalid format";
  };

  const handleInput = () => {
    if (combinedStrings.value && pattern.test(combinedStrings.value)) {
      const parts = combinedStrings.value.split(" ");
      regionFilter.value = parts[0]?.trim() || "";
      vbFilter.value = parts[1]?.trim() || "";
      kstFilter.value = parts[2]?.trim() || "";
    } else {
      regionFilter.value = "";
      vbFilter.value = "";
      kstFilter.value = "";
    }
  };

  const onClearClicked = () => {
    combinedStrings.value = "";
    regionFilter.value = "";
    vbFilter.value = "";
    kstFilter.value = "";
  };

  const router = useRouter();

  const goToOrder = (event: CalendarEvent) => {
    event.nativeEvent.stopPropagation();
    const routeData = router.resolve({
      name: "OrderInfo",
      params: {
        orderId: event.event.orderId as unknown as string
      }
    });
    window.open(routeData.href, "_blank");
  };

  const goToCalendarData = (date: string) => {
    clickedCalendarData.value = calendarData.value.find(item => item.date === date);
  };

  const createDialog = ref(false);
  const maxPosDialog = ref(false);
  const clickedDay = ref<string | undefined>(undefined);

  const checkIfOrderExists = (itemId: number): boolean => {
    return orders.value.some((item) => {
      return item.id === itemId;
    });
  };

  const reset = () => {
    clickedDay.value = undefined;
    calendar.value?.checkChange();
    calendarKey.value += 1;
    createDialog.value = false;
    maxPosDialog.value = false;
    clickedOrder.value = undefined;
    clickedCalendarData.value = undefined;
  };

  const close = () => {
    reset();
  };

  const saveInv = (orderResponse: Order, oldData: Order) => {
    if (orderResponse) {
      if (!checkIfOrderExists(orderResponse.id)) {
        orders.value.push(orderResponse);
      } else {
        const index = orders.value.findIndex(item => item.id === orderResponse.id);
        orders.value[index] = orderResponse;
      }

      updateInvCalendarData(orderResponse, oldData);
    }
    reset();
  };

  const updateInvCalendarData = (data: Order, old: Order) => {
    // update calendar data for inventory time span
    const from = moment.min(moment(data.validFrom), moment(old.validFrom)).format("YYYY-MM-DD");
    const to = moment.max(moment(data.validTo), moment(old.validTo)).format("YYYY-MM-DD");
    loadCalendarData(from, to ? to : from);
  };

  const saveCalendarData = (data: CalendarData[]) => {
    data.forEach(newItem => {
      const indexDate = calendarData.value.findIndex(existingItem => existingItem.date === newItem.date);
      if (indexDate !== -1) {
        calendarData.value.splice(indexDate, 1);
      }
      calendarData.value.push(newItem);
    });

    reset();
  };

  const createInventory = (event: any = null) => {
    if (event != null) {
      clickedDay.value = event.date;
    } else {
      // set clicked day to selected day
      if (type.value == "day") {
        const date = calendar?.value?.$data?.["lastStart"]?.date;
        if (date) {
          clickedDay.value = date;
        }
      }
    }
    createDialog.value = true;
  };

  const getPosOfDate = (date: string) => {
    let pos = 0;
    const data = calendarData.value.find(data => {
      return data.date?.toString() === date;
    });
    if (data) {
      pos = data.positions ? data.positions : 0;
    }
    return pos;
  };

  const getMaxPosOfDate = (date: string) => {
    let maxPos = defaultMaxPos.value;
    const data = calendarData.value.find(data => {
      return data.date?.toString() === date;
    });
    if (data) {
      maxPos = (data.maxPositions && data.maxPositions > 0) ? data.maxPositions : defaultMaxPos.value;
    }
    return maxPos;
  };

  const positionsAvailable = (dateStr: string) => {
    if (props.customer) {
      const date = moment(dateStr, "YYYY-MM-DD");
      const now = moment(moment.now()).format("YYYY-MM-DD");
      if (date.isBefore(now)) {
        return false;
      } else {
        const maxPos = getMaxPosOfDate(dateStr);
        let pos = getPosOfDate(dateStr);
        pos += props.positions ? props.positions : 0;
        return pos <= maxPos;
      }
    } else {
      return true;
    }
  };

  const editDayMaxPos = (date: string, event: Event) => {
    event.stopPropagation();
    clickedDay.value = date;
    goToCalendarData(date);
    maxPosDialog.value = true;
  };

  const getNoteOnDate = (date: string) => {
    const data = calendarData.value.find(data => {
      return data.date?.toString() === date;
    });
    return data?.note ? data?.note : null;
  };

  const buildPositionsLabel = (date: string) => {
    let maxPos = defaultMaxPos.value;
    let pos = 0;
    const data = calendarData.value.find(data => {
      return data.date?.toString() === date;
    });
    if (data) {
      maxPos = (data.maxPositions && data.maxPositions > 0) ? data.maxPositions : defaultMaxPos.value;
      pos = data.positions ? data.positions : 0;
    }
    const maxPosFormatted = new Intl.NumberFormat("de-DE").format(maxPos);
    const posFormatted = new Intl.NumberFormat("de-DE").format(pos);
    return `${posFormatted} / ${maxPosFormatted}`;
  };


return { __sfc: true,props, initialState, state, clickedOrder, clickedCalendarData, calendarKey, calendar, focus, type, regionFilter, vbFilter, kstFilter, combinedStrings, customerType, customerTypes, typeToLabel, orders, calendarData, defaultMaxPos, dataCache, customerCategories, isCustomerCategoryDefined, title, weekDayFormat, customNext, customPrev, events, loadDefaultMaxPos, loadOrders, exportOrders, loadCalendarData, currentMonthYear, inventoryCount, setDefaultCustomerType, viewDay, setToday, pattern, maskRule, handleInput, onClearClicked, router, goToOrder, goToCalendarData, createDialog, maxPosDialog, clickedDay, checkIfOrderExists, reset, close, saveInv, updateInvCalendarData, saveCalendarData, createInventory, getPosOfDate, getMaxPosOfDate, positionsAvailable, editDayMaxPos, getNoteOnDate, buildPositionsLabel, VCalendar, VCol, t, InvCreateDialog, EditMaxPosDialog, FabButton, MonthPicker, yearMonth }
}

})